// The impersonation strip: the amber band an operator reads while they play
// a player's account (deployment.md, the impersonation section).
//
// An operator who answers a support ticket signs in as the account with a
// one-time code, and is then that player everywhere — on the landing
// screen, in the hub and at sea. That is the whole point of the flow, and
// it is also its one hazard: an operator who forgets which window they are
// in can act on somebody else's empire. So the client says so, on every
// screen, for the whole life of the session.
//
// Three rules the shape of the client forces:
//
//  - **It is a fourth root.** `#landing` and `#hub` each take their own
//    root off the body when they resolve, and `#ui` is built by the world.
//    A strip that lived inside any of the three would leave with it. So
//    `mountImpBar` appends `#impbar` to the body itself, once, before the
//    entry resolves, and no screen ever owns it.
//
//  - **It never takes a pointer.** The strip lies over the map, the hub
//    and the arcade board at the top of the glass. `pointer-events: none`
//    holds for the strip and `auto` for the STOP button alone, so the one
//    thing under it a person can press is the thing they meant to press.
//
//  - **STOP reloads.** The client holds the account in a screen, in the
//    state cache and in a live socket. Ending the session under all three
//    would leave a client acting as a player it no longer is, so the press
//    ends the session and then reloads the page.
//
// The module writes no sentence of its own: the two words come from
// `gen/strings.ts` like every word the DOM writes, and every distance and
// every colour is in `ui/styles.css`. `impbar.test.ts` fails when the
// module and the stylesheet drift apart.

import { esc } from './esc';
import { adoptLightRoot } from './lightchrome';
import { stopImpersonating, type Me } from '../net/api';
import { impersonation as S } from '../gen/strings';

/** What one strip says. A plain argument object: the strip reads no global
 *  state, and both strings are escaped before they reach the glass. */
export interface ImpCard {
  /** The player whose account this session plays. */
  name: string;
  /** The operator token the meta server put on the session, `ops:<user>`. */
  operator: string;
}

/**
 * The strip's markup. Exported for the test, and pure.
 *
 * The line names the player and the operator; the button is the one
 * control. Both strings arrive from the meta server, so both are escaped.
 */
export function impBarMarkup(card: ImpCard): string {
  const line = S.banner({ name: esc(card.name), operator: esc(card.operator) });
  return `<span class="who">${line}</span>`
    + `<button type="button" class="stop">${S.stop}</button>`;
}

/** What the strip does when a person presses STOP. The client passes none
 *  and gets the real pair; the test passes its own and watches them. */
export interface ImpBarDeps {
  stop: () => Promise<unknown>;
  reload: () => void;
}

/** What the browser hands `ImpBar` when the caller names nothing. */
function pageDeps(): ImpBarDeps {
  return { stop: stopImpersonating, reload: () => location.reload() };
}

/**
 * The controller `main.ts` composes: one element, raised once when the
 * account carries an operator and never raised again.
 *
 * The class it sets on that element is its whole interface to the
 * stylesheet: `on` is the strip up. Nothing else moves.
 */
export class ImpBar {
  private up = false;
  private presser: ((e: Event) => void) | null = null;

  constructor(
    private readonly el: HTMLElement,
    private readonly deps: ImpBarDeps = pageDeps(),
  ) {}

  /** Presentation-only; the tests and the browser check read it. */
  get showing(): boolean { return this.up; }

  /**
   * Raises the strip, if this session is an impersonated one. It answers
   * whether it did, because the caller hangs it off `GET /api/me` and a
   * server that predates the field answers no operator at all.
   *
   * An empty operator is an ordinary session and raises nothing. A second
   * call while the strip is up raises nothing either: the session cannot
   * change under a page that is already running.
   */
  raise(card: ImpCard): boolean {
    if (this.up || card.operator.trim() === '') return false;
    this.el.innerHTML = impBarMarkup(card);
    this.el.classList.add('on');
    this.up = true;

    // On pointerdown, as every other control of the chrome acts
    // (ui-map-interface.md §10): a button destroyed between the press and
    // the release swallows the click, and this one destroys its own page.
    const press = (ev: Event) => {
      const target = ev.target;
      if (!(target instanceof Element) || target.closest('.stop') === null) return;
      ev.preventDefault();
      void this.stop();
    };
    this.presser = press;
    this.el.addEventListener('pointerdown', press);
    return true;
  }

  /**
   * Ends the session and reloads the page.
   *
   * The reload happens whether the call answered or refused. A session the
   * server already dropped, a socket that timed out, a meta server that
   * went away: in every one of them the operator asked to stop being this
   * player, and a page that stayed up as that player would be the worst of
   * the three answers.
   */
  async stop(): Promise<void> {
    // The button goes down first, so a second press cannot open a second
    // call while the first one is still in flight.
    this.el.classList.add('stopping');
    try {
      await this.deps.stop();
    } catch {
      // Swallowed on purpose, and it is the only refusal this client
      // ignores. The reload below is what actually takes the operator out
      // of the account, because the page comes back with whatever cookie
      // the browser still holds.
    }
    this.deps.reload();
  }

  /** For a teardown of the whole client. */
  destroy(): void {
    this.up = false;
    if (this.presser) {
      this.el.removeEventListener('pointerdown', this.presser);
      this.presser = null;
    }
    this.el.classList.remove('on', 'stopping');
    this.el.innerHTML = '';
  }
}

/**
 * The one strip of the page.
 *
 * It is a module singleton for the reason `ui/chart/cartouche.ts` holds
 * one: the page's `boot()` builds the strip, and the code that learns
 * WHOSE session this is sits inside `fast/entry.ts`, which the boot is
 * waiting on. Neither of the two can hand the other a reference without a
 * third owner, so the module itself is that owner.
 */
let bar: ImpBar | null = null;

/**
 * Puts the strip's root on the page and hands back the controller.
 *
 * Both pages that hold an account call this before they resolve an entry,
 * because the entry is what removes and replaces the screen roots. Calling
 * it twice mounts no second strip: the element is found by id.
 */
export function mountImpBar(deps?: ImpBarDeps): ImpBar {
  let el = document.getElementById('impbar');
  if (el === null) {
    el = document.createElement('div');
    el.id = 'impbar';
    // The strip stays on the page: a root of its own in the light
    // identity (ui-map-interface.md §2.5, R3320).
    adoptLightRoot(el);
    document.body.appendChild(el);
  }
  bar = new ImpBar(el, deps);
  return bar;
}

/**
 * Raises the mounted strip for the account this browser answers as.
 *
 * It takes the whole `Me` and reads two fields of it, so no caller has to
 * know the shape of the card. An account with no operator on it, a page
 * with no session at all and a page that mounted no strip are the three
 * ordinary answers, and all three raise nothing.
 */
export function showImpBar(me: Me | null): boolean {
  const operator = me?.impersonatedBy ?? '';
  if (bar === null || me === null || operator === '') return false;
  return bar.raise({ name: me.account.name, operator });
}

/** For a test that mounts a strip of its own. The page never calls it. */
export function resetImpBar(): void {
  bar = null;
}
